import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MessageSquare, Star, Send, CheckCircle } from 'lucide-react';

const CATEGORIES = [
  { id: 'bug', label: '🐛 Bug Report' },
  { id: 'feature', label: '💡 Feature Request' },
  { id: 'improvement', label: '⚡ Improvement' },
  { id: 'general', label: '💬 General Feedback' },
  { id: 'praise', label: '🙌 Praise' },
];

export default function FeedbackPage() {
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (!message.trim()) { toast.error('Please write your feedback'); return; }
    setSubmitting(true);
    try {
      await axios.post('/api/account/feedback', { category, message, rating: rating || null });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch { toast.error('Failed to submit — please email support@ignitorads.com'); }
    finally { setSubmitting(false); }
  }

  if (submitted) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'color-mix(in srgb, var(--green) 10%, transparent)', border: '2px solid var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <CheckCircle size={32} style={{ color: 'var(--green)' }} />
      </div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>Feedback Received!</h2>
      <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '400px', lineHeight: 1.7, marginBottom: '24px' }}>
        Thank you for taking the time to share your thoughts. We read every piece of feedback and use it to improve IgnitorAds.
      </p>
      <button className="btn btn-secondary" onClick={() => { setSubmitted(false); setMessage(''); setRating(0); setCategory('general'); }}>
        Submit Another
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:'color-mix(in srgb, var(--purple) 10%, transparent)', border:'1px solid color-mix(in srgb, var(--purple) 30%, transparent)', borderRadius:'var(--radius)', padding:'10px 18px', marginBottom:'20px' }}>
        <span style={{ fontSize:'18px' }}>🚧</span>
        <div>
          <div style={{ fontWeight:700, fontSize:'13px', color:'var(--purple)' }}>Coming Soon</div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>This feature is under development. In the meantime, email us at <span style={{ color:'var(--cyan)' }}>support@ignitorads.com</span></div>
        </div>
      </div>
      <h1 className="page-title">Provide Feedback</h1>
      <p className="page-subtitle">Help us improve IgnitorAds. Your feedback goes directly to the team.</p>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '24px' }}>

        {/* Category */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '10px', fontWeight: 700 }}>Category</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setCategory(c.id)}
                className={`btn btn-sm ${category === c.id ? 'btn-primary' : 'btn-secondary'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Star Rating */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '10px', fontWeight: 700 }}>
            Overall Rating (optional)
          </label>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setRating(s === rating ? 0 : s)}
                onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                <Star size={28} fill={(hoverRating || rating) >= s ? 'var(--amber)' : 'none'}
                  style={{ color: (hoverRating || rating) >= s ? 'var(--amber)' : 'var(--text-muted)', transition: 'all 0.1s' }} />
              </button>
            ))}
            {rating > 0 && <span style={{ fontSize: '12px', color: 'var(--text-muted)', alignSelf: 'center', marginLeft: '8px' }}>
              {['','Poor','Fair','Good','Great','Excellent'][rating]}
            </span>}
          </div>
        </div>

        {/* Message */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px', fontWeight: 700 }}>Your Feedback *</label>
          <textarea className="input-field" style={{ width: '100%', minHeight: '140px', resize: 'vertical', lineHeight: 1.6 }}
            placeholder="Describe your experience, what's working well, what could be improved, or any bugs you encountered..."
            value={message} onChange={e => setMessage(e.target.value)} />
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>{message.length} characters</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Or email us directly at <span style={{ color: 'var(--cyan)' }}>support@ignitorads.com</span>
          </div>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || !message.trim()}>
            {submitting ? 'Submitting...' : <><Send size={13} /> Submit Feedback</>}
          </button>
        </div>
      </div>
    </div>
  );
}
